import Modal, { ModalBody, ModalHeader, ModalTransition } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { Divider, Heading, Text, Button, ButtonAppearance, Icon, Link } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useBreakpoint } from 'src/hooks/useBreakpoint';
import { type OtherSession } from 'src/types/classSession';

import { ClassTimeSlot } from './ClassTimeSlot';

interface Props {
  courseName: string;
  sessions: OtherSession[];
}

export const FindOtherClasses = ({ courseName, sessions }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();

  if (sessions.length === 0) {
    return null;
  }

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const handleViewDetails = (e: React.MouseEvent, detailsUrl: string) => {
    e.preventDefault();
    onClose();
    navigate(detailsUrl);
  };

  return (
    <>
      <Button
        label="Find other classes"
        appearance={ButtonAppearance.SECONDARY}
        iconAfter={<Icon type="arrow-right" />}
        onClick={onOpen}
      />
      <ModalTransition>
        {isOpen && (
          <Modal onClose={onClose} width="large">
            <ModalHeader hasCloseButton>
              <div css={modalHeaderContentStyles}>
                <Heading level={4}>{courseName}</Heading>
                <Text size="large" color={token('color.text.subtle')}>
                  Upcoming class dates and time
                </Text>
              </div>
            </ModalHeader>
            <ModalBody>
              <div css={contentSectionStyles}>
                <Divider color={token('color.border')} />
                <div css={sessionsContainerStyles}>
                  {sessions.map((session) => (
                    <div key={session.id} css={sessionItemStyles}>
                      <ClassTimeSlot
                        iconMonth={session.month}
                        iconDay={session.day}
                        titleDate={session.label}
                        subtitleTime={session.time}
                      />
                      <div css={actionsContainerStyles}>
                        <span css={linkWrapperStyles}>
                          <Link
                            link={session.detailsUrl}
                            label="View details"
                            onClick={(e) => handleViewDetails(e, session.detailsUrl)}
                          />
                        </span>
                        <Button
                          fullWidth={isMobile}
                          label="Register on partner site"
                          appearance={ButtonAppearance.SECONDARY}
                          iconAfter={<Icon type="arrow-right" />}
                          onClick={() => window.open(session.partnerSiteUrl, '_blank')}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ModalBody>
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

const modalHeaderContentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  paddingTop: token('space.300'),
  paddingLeft: token('space.100'),
  paddingRight: token('space.100'),
});

const contentSectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300'),
  paddingBottom: token('space.400'),
});

const sessionsContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.600'),
  '@media (min-width: 800px)': {
    gap: token('space.300'),
  },
});

const sessionItemStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  '@media (min-width: 660px)': {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

const actionsContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: token('space.150'),
  '@media (min-width: 800px)': {
    flexDirection: 'row',
    gap: token('space.300'),
  },
});

const linkWrapperStyles = css({
  whiteSpace: 'nowrap',
});
